import React from 'react';
import { Link } from '@amzn/awsui-components-console';

// Matches http(s) URLs. Used with String.split (capturing group) so the URL
// segments are preserved in the result array.
const URL_SPLIT_REGEX = /(https?:\/\/[^\s]+)/g;
// Punctuation that commonly trails a URL inside a sentence (e.g. the ")" and
// "," in "(see the guide: https://…#groups), then resubmit.") — stripped off the
// href so it isn't swallowed into the link.
const TRAILING_PUNCTUATION = /[.,;:!?)\]}'"]+$/;

// Render a plain-text segment, converting embedded newlines to <br/> so
// multi-line content renders on separate lines. The renderers below display the
// content under the default `white-space: normal` (e.g. the Cloudscape Flashbar
// container `.awsui_flash`), which collapses a "\n" to a single space — so a
// multi-line message (like a list of pre-submit validation errors) would
// otherwise render as one run-on line. A single-line segment is returned as the
// raw string so plain text (and string-based test assertions) is unaffected.
const withLineBreaks = (text: string, keyPrefix: string): React.ReactNode => {
  if (!text.includes('\n')) {
    return text;
  }
  return text.split('\n').map((line, i) => (
    <React.Fragment key={`${keyPrefix}n${i}`}>
      {i > 0 && <br />}
      {line}
    </React.Fragment>
  ));
};

/**
 * Convert a plain string into a ReactNode where any http(s) URL becomes a
 * clickable external link and any embedded newline becomes a line break. A
 * single-line string without a URL is returned unchanged so plain text (and
 * string-based test assertions) are unaffected.
 *
 * Shared so every place that renders a server- or client-provided message — API
 * error banners (ApiErrorContext), the sticky build-submit error bar
 * (AsyncErrorBar), and the per-build "Message" line in the Build Status details
 * (BuildIdsMapComponent) — turns an embedded link (e.g. the Fusion User Guide
 * build-permissions URL the service appends to a missing-permission error) into
 * a clickable link, and renders a multi-line message (e.g. several pre-submit
 * validation errors joined with "\n") on separate lines rather than a single
 * run-on line.
 */
export const linkify = (content: string): React.ReactNode => {
  const parts = content.split(URL_SPLIT_REGEX);
  if (parts.length === 1) {
    // No URL: keep the plain-string contract for single-line text; split
    // multi-line text into <br/>-separated lines.
    return withLineBreaks(content, 't0');
  }
  return parts.map((part, i) => {
    if (!/^https?:\/\//.test(part)) {
      return (
        <React.Fragment key={`t${i}`}>
          {withLineBreaks(part, `t${i}`)}
        </React.Fragment>
      );
    }
    const trailing = TRAILING_PUNCTUATION.exec(part)?.[0] ?? '';
    const url = trailing ? part.slice(0, part.length - trailing.length) : part;
    return (
      <React.Fragment key={i}>
        <Link href={url} external>
          {url}
        </Link>
        {trailing}
      </React.Fragment>
    );
  });
};
